import React, { useState, useEffect, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, storage } from '../firebase/config';
import { ArrowLeft, Send, Sparkles, Camera, ImageIcon, X, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import ProsthesisSelector from './ProsthesisSelector';
import DigitalFileUpload from './DigitalFileUpload';

/**
 * A/S 보철 의뢰서 작성 (/test-cases/create, 치과 전용)
 * - 치과에서는 "A/S 보철"로, 기공소에는 "테스트보철"로 전달됨
 * - 주문 의뢰서와 동일 구성: 보철 선택기 + 이미지 + 특이사항(빠른입력) + 디지털 파일
 * - 거래처 선택 없음 / 공개: 요약만 / 비공개: 의뢰서 상세·이미지·파일 (수락 기공소에게 공개)
 * - 무료 (차감권 제도 없음)
 */
const REGIONS = ['서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산', '세종', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'];

// 주문 의뢰서(CreateOrder)와 동일한 기본 보철 옵션
const DEFAULT_PROSTHESIS_OPTIONS = [
    { label: 'Inlay', value: 'Inlay' },
    { label: 'Crown', value: 'Crown' },
    { label: 'POST', value: 'POST' },
    { label: 'Denture', value: 'Denture' },
    { label: '교정', value: '교정' },
    { label: '장치', value: '장치' },
];
const DEFAULT_MATERIAL_MAP = {
    'Inlay': ['레진', '골드', '세라믹', '지르코니아', '하이브리드레진', '기타'],
    'Crown': ['지르코니아', 'PFM', 'PFZ', '메탈', '골드', '세라믹', '기타'],
    'POST': ['메탈', '지르코니아', '기타'],
    'Denture': ['CDO', 'RPD', 'VALPLAST', 'Temp Denture', 'Wire Temporary', '기타'],
    '교정': ['교정장치', '유지장치', '기타'],
    '장치': ['마우스가드', '이갈이장치', '기타'],
};
const DEFAULT_IMPLANT_OPTIONS = [
    { label: 'Osstem TS Mini', value: 'Osstem TS Mini' },
    { label: 'Osstem TS Regular', value: 'Osstem TS Regular' },
    { label: 'Osstem TS Wide', value: 'Osstem TS Wide' },
    { label: 'Osstem KS', value: 'Osstem KS' },
];
// 주문 의뢰서와 동일한 특이사항 빠른입력
const NOTE_SHORTCUTS = ['급한 케이스', '재제작', '색상 확인 필요', '형태 주의', '교합 확인', '환자 요청사항'];

const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6, color: '#374151' };

// 주문 의뢰서(CreateOrder)와 동일한 납기일 달력 스타일
const CAL = {
    selectBox: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', backgroundColor: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', padding: '0 12px', height: 48, cursor: 'pointer' },
    dueDateBadge: { backgroundColor: '#fef3c7', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: '700', color: '#d97706' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modal: { backgroundColor: '#fff', borderRadius: 16, width: '90%', maxWidth: 400, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 18, borderBottom: '1px solid #f1f5f9' },
    modalTitle: { fontSize: 17, fontWeight: '700', color: '#0f172a' },
    modalCloseBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 4 },
    modalContent: { padding: 18, overflowY: 'auto' },
    nav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    navBtn: { width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#64748b' },
    month: { fontSize: 16, fontWeight: '700', color: '#0f172a' },
    weekHeader: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 8 },
    weekDay: { textAlign: 'center', fontSize: 12, fontWeight: '600', padding: '8px 0' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 },
    day: { aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: '500', color: '#374151', cursor: 'pointer' },
    daySelected: { backgroundColor: '#6366f1', color: '#ffffff' },
    dayDisabled: { backgroundColor: 'transparent', color: '#cbd5e1', cursor: 'default' },
};

function CreateTestRequest({ user }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // 지정 의뢰 — 기공소 프로필에서 진입 시 ?lab={labId}&labName={이름}
    const invitedLabId = searchParams.get('lab') || '';
    const invitedLabName = searchParams.get('labName') || '';
    const [submitting, setSubmitting] = useState(false);

    // 보철 선택기 상태 (주문 의뢰서와 동일 구조)
    const [selectedTeeth, setSelectedTeeth] = useState([]);
    const [prosthesisGroups, setProsthesisGroups] = useState([]);
    const [bridgeGroups, setBridgeGroups] = useState([]);
    const [ponticGroups, setPonticGroups] = useState([]);
    const [implantGroups, setImplantGroups] = useState([]);
    const [teethDetails, setTeethDetails] = useState({});

    const [shade, setShade] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [region, setRegion] = useState('');
    const [specialNotes, setSpecialNotes] = useState('');
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [calendarDate, setCalendarDate] = useState(new Date());

    // 이미지 (카메라/갤러리) — 비공개, 수락 기공소에게 공개
    const [images, setImages] = useState([]); // [{ file, preview }]
    const cameraInputRef = useRef(null);
    const galleryInputRef = useRef(null);

    // 디지털 파일 (STL/ZIP) — 비공개
    const [digitalFiles, setDigitalFiles] = useState([]);
    // 업로드 폴더 식별자 (마운트 시 1회 고정 — 사용자·세션별 충돌 방지)
    const uploadSessionRef = useRef(`testreq_${auth.currentUser?.uid || 'anon'}_${Date.now()}`);

    const businessType = user?.businessType || user?.userType;
    const isDental = businessType === 'dental' || businessType === 'clinic';

    if (!isDental) {
        return (
            <div style={{ maxWidth: 640, margin: '60px auto', textAlign: 'center', color: '#6B7280' }}>
                A/S 보철 의뢰는 치과 회원만 이용할 수 있습니다.
            </div>
        );
    }

    // 희망납기 빠른 선택
    const setDueInDays = (days) => {
        const d = new Date();
        d.setDate(d.getDate() + days);
        setDueDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    };
    const QUICK_DUE = [[7, '1주일'], [10, '10일'], [14, '2주일'], [21, '3주일'], [30, '1개월']];

    // 납기일 Date 객체 + D-day (주문 의뢰서와 동일 표기)
    const dueDateObj = dueDate ? new Date(dueDate + 'T00:00:00') : null;
    const dDayCount = dueDateObj ? Math.max(0, Math.ceil((dueDateObj - new Date()) / 86400000)) : null;

    // 달력 헬퍼 (주문 의뢰서와 동일)
    const getCalendarDays = () => {
        const year = calendarDate.getFullYear();
        const month = calendarDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const days = [];
        for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
        for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
        return days;
    };
    const isDateDisabled = (date) => {
        if (!date) return true;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    };
    const isSameDay = (d1, d2) => {
        if (!d1 || !d2) return false;
        return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
    };
    const pickDate = (date) => {
        setDueDate(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
        setShowDatePicker(false);
    };

    // 특이사항 빠른입력 (주문 의뢰서와 동일 동작)
    const appendShortcut = (text) => {
        setSpecialNotes(prev => prev ? `${prev}, ${text}` : text);
    };

    // 이미지 추가/삭제
    const handleImageFiles = (fileList) => {
        const files = Array.from(fileList || []);
        const next = files.map(file => ({ file, preview: URL.createObjectURL(file) }));
        setImages(prev => [...prev, ...next].slice(0, 10)); // 최대 10장
    };
    const removeImage = (idx) => {
        setImages(prev => prev.filter((_, i) => i !== idx));
    };

    // 공개용 요약 생성 (보철 종류·치식·재료만 — 상세는 비공개)
    const buildPublicSummary = () => {
        const parts = [];
        prosthesisGroups.forEach(g => parts.push(`${g.prosthesis}(${g.material}) ${g.teeth.length}본`));
        implantGroups.forEach(g => parts.push(`임플란트${g.brand ? `(${g.brand})` : ''} ${g.teeth.length}본`));
        if (bridgeGroups.length > 0) parts.push(`브릿지 ${bridgeGroups.length}개`);
        if (ponticGroups.length > 0) parts.push(`폰틱 ${ponticGroups.reduce((s, g) => s + g.teeth.length, 0)}본`);
        const materials = [...new Set(prosthesisGroups.map(g => g.material).filter(Boolean))];
        return {
            prosthesisType: parts.join(', ') || '보철',
            teeth: selectedTeeth.map(String),
            material: materials.join(', '),
            shade,
            dueDate,
            notes: specialNotes,
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedTeeth.length === 0) { alert('치식도에서 치아를 선택하고 보철물을 설정해주세요.'); return; }
        if (!window.confirm('A/S 재제작 상담 의뢰를 등록할까요?')) return;

        setSubmitting(true);
        try {
            // 이미지 업로드 (비공개 저장소)
            const uid = auth.currentUser.uid;
            const imageUrls = [];
            for (const img of images) {
                const storageRef = ref(storage, `testRequests/${uid}/${Date.now()}_${Math.random().toString(36).substring(7)}.jpg`);
                await uploadBytes(storageRef, img.file);
                imageUrls.push(await getDownloadURL(storageRef));
            }

            const functions = getFunctions(undefined, 'asia-northeast3');
            const create = httpsCallable(functions, 'createTestRequest');
            const res = await create({
                caseInfo: buildPublicSummary(),
                region,
                attachments: imageUrls,
                ...(invitedLabId ? { invitedLabId } : {}),
                // 의뢰서 상세 (비공개 → 수락 기공소에게 전체 공개)
                orderDetail: {
                    prosthesisGroups, bridgeGroups, ponticGroups, implantGroups,
                    teethDetails, selectedTeeth, shade, dueDate,
                    specialNotes,
                    digitalFiles: digitalFiles.length > 0 ? digitalFiles : [],
                },
            });
            alert(invitedLabId
                ? `${invitedLabName || '지정 기공소'}에게 지정 의뢰가 등록되었습니다.\n테스트번호: ${res.data.testNumber}\n(해당 기공소만 열람·수락할 수 있습니다)`
                : `A/S 재제작 상담 의뢰가 등록되었습니다.\n테스트번호: ${res.data.testNumber}`);
            navigate(`/test-cases/${res.data.requestId}`);
        } catch (err) {
            console.error('의뢰 등록 실패:', err);
            alert(err.message || '의뢰 등록에 실패했습니다.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
            <button onClick={() => navigate('/test-cases')} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 14, marginBottom: 16, padding: 0 }}>
                <ArrowLeft size={16} /> A/S 보철 의뢰 목록
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>A/S 재제작 상담 의뢰서</h1>
            </div>

            {/* 지정 의뢰 배너 — 기공소 프로필에서 진입 */}
            {invitedLabId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#EEF2FF', border: '1.5px solid #C7D2FE', borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: 14, fontWeight: 700, color: '#3730A3' }}>
                    🎯 {invitedLabName ? `"${invitedLabName}"` : '선택한 기공소'} 지정 의뢰 — 이 의뢰는 해당 기공소에게만 전달되며, 다른 기공소에는 공개되지 않습니다.
                </div>
            )}

            {/* A/S → 테스트보철 안내 카드 */}
            <div style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F5F3FF 100%)', border: '1px solid #BFDBFE', borderRadius: 14, padding: '18px 20px', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 15, color: '#1E40AF', marginBottom: 8 }}>
                    <Sparkles size={18} /> A/S 재제작 상담 — 부담은 줄이고, 좋은 기공소는 새로 만나세요
                </div>
                <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.8, margin: 0 }}>
                    A/S 재제작 상담은 <b>덴탈클러스터를 통해 진행된 의뢰서를 기반</b>으로 진행됩니다.
                    작성하신 의뢰서는 기공소에 <b>테스트보철 케이스</b>로 전달되며,
                    기공소에게는 새로운 치과와 연결될 소중한 기회이기에 어느 케이스보다 정성껏 제작됩니다.
                    원장님은 부담 없이 맡기시고,
                    결과물이 마음에 드는 기공소를 만나면 그대로 정식 거래처로 이어가세요.
                    치과·환자 정보는 기공소가 수락하기 전까지 공개되지 않습니다.
                </p>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* 보철물 설정 — 주문 의뢰서와 동일한 선택기 */}
                <div>
                    <label style={labelStyle}>보철물 설정 * <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(주문 의뢰서와 동일한 방식입니다)</span></label>
                    <ProsthesisSelector
                        prosthesisGroups={prosthesisGroups}
                        setProsthesisGroups={setProsthesisGroups}
                        bridgeGroups={bridgeGroups}
                        setBridgeGroups={setBridgeGroups}
                        ponticGroups={ponticGroups}
                        setPonticGroups={setPonticGroups}
                        implantGroups={implantGroups}
                        setImplantGroups={setImplantGroups}
                        teethDetails={teethDetails}
                        setTeethDetails={setTeethDetails}
                        selectedTeeth={selectedTeeth}
                        setSelectedTeeth={setSelectedTeeth}
                        prosthesisOptions={DEFAULT_PROSTHESIS_OPTIONS}
                        prosthesisMaterialMap={DEFAULT_MATERIAL_MAP}
                        implantOptions={DEFAULT_IMPLANT_OPTIONS}
                        storageKey={user?.companyId || user?.uid || ''}
                    />
                </div>

                {/* 이미지 — 카메라 / 갤러리 (주문 의뢰서와 동일) */}
                <div>
                    <label style={labelStyle}>이미지 <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(비공개 — 수락한 기공소에게만 공개)</span></label>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                        <button type="button" onClick={() => cameraInputRef.current?.click()}
                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px', border: '1px dashed #93C5FD', borderRadius: 10, background: '#EFF6FF', color: '#2563EB', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                            <Camera size={18} /> 카메라
                        </button>
                        <button type="button" onClick={() => galleryInputRef.current?.click()}
                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px', border: '1px dashed #93C5FD', borderRadius: 10, background: '#EFF6FF', color: '#2563EB', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                            <ImageIcon size={18} /> 갤러리
                        </button>
                    </div>
                    <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                        onChange={e => { handleImageFiles(e.target.files); e.target.value = ''; }} />
                    <input ref={galleryInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
                        onChange={e => { handleImageFiles(e.target.files); e.target.value = ''; }} />
                    {images.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {images.map((img, i) => (
                                <div key={i} style={{ position: 'relative' }}>
                                    <img src={img.preview} alt={`첨부 ${i + 1}`} style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E7EB' }} />
                                    <button type="button" onClick={() => removeImage(i)}
                                        style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: '50%', background: '#DC2626', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <X size={13} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* 특이사항 + 빠른입력 (주문 의뢰서와 동일) */}
                <div>
                    <label style={labelStyle}>특이사항 <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(공개 — 치과명·환자명은 적지 마세요)</span></label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                        {NOTE_SHORTCUTS.map(s => (
                            <button type="button" key={s} onClick={() => appendShortcut(s)}
                                style={{ padding: '7px 12px', borderRadius: 16, fontSize: 13, fontWeight: 500, cursor: 'pointer', border: '1px solid #D1D5DB', background: '#fff', color: '#374151' }}>
                                {s}
                            </button>
                        ))}
                    </div>
                    <textarea value={specialNotes} onChange={e => setSpecialNotes(e.target.value)} rows={4}
                        placeholder="케이스 설명, 특이사항 등" style={{ ...inputStyle, resize: 'vertical' }} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                        <label style={labelStyle}>쉐이드</label>
                        <input value={shade} onChange={e => setShade(e.target.value)} placeholder="예: A2" style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>지역 (광역 단위, 공개)</label>
                        <select value={region} onChange={e => setRegion(e.target.value)} style={inputStyle}>
                            <option value="">비공개</option>
                            {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                </div>

                <div>
                    <label style={labelStyle}>희망 납기일</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                        {QUICK_DUE.map(([days, label]) => {
                            const d = new Date(); d.setDate(d.getDate() + days);
                            const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                            const active = dueDate === iso;
                            return (
                                <button type="button" key={days} onClick={() => setDueInDays(days)}
                                    style={{
                                        padding: '10px 16px', borderRadius: 20, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                                        border: active ? '2px solid #2563EB' : '1px solid #D1D5DB',
                                        background: active ? '#EFF6FF' : '#fff',
                                        color: active ? '#2563EB' : '#374151',
                                    }}>
                                    {label} 후
                                </button>
                            );
                        })}
                    </div>
                    {/* 주문 의뢰서와 동일한 선택 박스 + 달력 모달 */}
                    <button type="button" style={CAL.selectBox}
                        onClick={() => { setCalendarDate(dueDateObj || new Date()); setShowDatePicker(true); }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                            <Calendar size={16} color="#f59e0b" />
                            <span style={{ fontSize: 15, color: dueDateObj ? '#0f172a' : '#9ca3af', fontWeight: dueDateObj ? '500' : '400' }}>
                                {dueDateObj
                                    ? dueDateObj.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' })
                                    : '납기일 선택'}
                            </span>
                        </div>
                        {dDayCount !== null && <span style={CAL.dueDateBadge}>D-{dDayCount}</span>}
                    </button>
                </div>

                {/* 디지털 파일 (주문 의뢰서와 동일 컴포넌트) */}
                <div>
                    <label style={labelStyle}>디지털 파일 (선택) <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(비공개 — 수락한 기공소에게만 공개)</span></label>
                    <div style={{ fontSize: 12.5, color: '#D97706', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
                        파일명에 환자 이름이 들어가 있지 않은지 확인해주세요. (예: <s>홍길동_crown.stl</s> → T-번호_crown.stl)
                    </div>
                    <DigitalFileUpload
                        orderId={uploadSessionRef.current}
                        user={user}
                        onFilesChange={setDigitalFiles}
                    />
                </div>

                <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#6B7280', lineHeight: 1.7 }}>
                    · 게시판에는 <b>보철 요약·치식·재료·납기·지역·특이사항</b>만 공개됩니다.<br />
                    · 의뢰서 상세·이미지·디지털 파일과 치과 정보는 <b>기공소가 수락한 후에만</b> 해당 기공소에 공개됩니다.<br />
                    · 환자 식별번호는 테스트번호(예: T-202607-0001)로 자동 생성되며, 환자 개인정보는 수집하지 않습니다.
                </div>

                <button
                    type="submit"
                    disabled={submitting}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: submitting ? '#93C5FD' : '#2563EB', color: '#fff', border: 'none', padding: '14px', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: submitting ? 'default' : 'pointer' }}
                >
                    <Send size={16} /> {submitting ? '등록 중...' : '상담 의뢰 등록'}
                </button>
            </form>

            {/* 납기일 달력 모달 (주문 의뢰서와 동일) */}
            {showDatePicker && (
                <div style={CAL.modalOverlay} onClick={() => setShowDatePicker(false)}>
                    <div style={CAL.modal} onClick={e => e.stopPropagation()}>
                        <div style={CAL.modalHeader}>
                            <span style={CAL.modalTitle}>납기일 선택</span>
                            <button type="button" onClick={() => setShowDatePicker(false)} style={CAL.modalCloseBtn}>
                                <X size={24} color="#64748b" />
                            </button>
                        </div>
                        <div style={CAL.modalContent}>
                            <div style={CAL.nav}>
                                <button type="button" style={CAL.navBtn}
                                    onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1))}>
                                    <ChevronLeft size={20} />
                                </button>
                                <span style={CAL.month}>
                                    {calendarDate.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' })}
                                </span>
                                <button type="button" style={CAL.navBtn}
                                    onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1))}>
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                            <div style={CAL.weekHeader}>
                                {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
                                    <span key={d} style={{ ...CAL.weekDay, color: i === 0 ? '#ef4444' : i === 6 ? '#3b82f6' : '#64748b' }}>{d}</span>
                                ))}
                            </div>
                            <div style={CAL.grid}>
                                {getCalendarDays().map((date, idx) => (
                                    <button
                                        type="button"
                                        key={idx}
                                        style={{
                                            ...CAL.day,
                                            ...(date && dueDateObj && isSameDay(date, dueDateObj) ? CAL.daySelected : {}),
                                            ...(isDateDisabled(date) ? CAL.dayDisabled : {}),
                                        }}
                                        onClick={() => { if (date && !isDateDisabled(date)) pickDate(date); }}
                                        disabled={isDateDisabled(date)}
                                    >
                                        {date ? date.getDate() : ''}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CreateTestRequest;
